import {useRef} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types'

import {Grid, Heading, Link, Text} from '@primer/react-brand/esm'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {ContentfulInlineFootnote} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnote'
import {ContentfulTestimonial} from '@github-ui/swp-core/components/contentful/ContentfulTestimonial'
import type {RichText} from '@github-ui/swp-core/schemas/contentful/richText'

import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

import {getGenericContentLink} from '../../../../brand/lib/types/contentful'
import type Assets from '../../webgl-utils/assets'

import SectionHero from '../../_components/SectionHero/SectionHero'
import SectionIntro from '../../_components/SectionIntro/SectionIntro'

import {RiverAccordion} from '../../_components/RiverAccordion/RiverAccordion'
import type {HomeCollaborationContent} from '../../content'

const ANALYTICS_ID = 'collaboration'

type Props = {
  contentfulContent: HomeCollaborationContent
  assetsRef: React.RefObject<Assets | null>
}

export function CollaborationSection(props: Props) {
  const {
    contentfulContent: {
      breakout: collaborationSectionBreakout,
      accordionItems: accordionEntries,
      sectionIntro,
      testimonial,
    },
    assetsRef,
  } = props

  const breakoutImage = collaborationSectionBreakout.fields.media?.find(
    item => item.fields.id === 'collaborationSectionBreakoutImage',
  )

  const accordionItems = accordionEntries.map(item => {
    const {heading, subheading, media} = item.fields
    const firstImage = media?.at(0)
    const firstLink = getGenericContentLink(item)

    return {
      title: heading || '',
      description: subheading ? documentToPlainTextString(subheading) : '',
      link: {
        label: firstLink?.fields.text || '',
        url: firstLink?.fields.href || '',
      },
      visual: {
        url: firstImage?.fields.asset.fields.file.url || '',
        alt: firstImage?.fields.description || '',
      },
    }
  })

  const mainGridRef = useRef<HTMLDivElement | null>(null)
  const {isIntersecting: isMainGridInView} = useIntersectionObserver(mainGridRef, {threshold: 0.33, isOnce: true})
  const breakoutLink = getGenericContentLink(collaborationSectionBreakout)

  function renderRichText(richText: RichText, fontSize: '300' | '400' = '400') {
    return documentToReactComponents(richText, {
      renderMark: {
        [MARKS.BOLD]: text => (
          <Text as="span" size={fontSize} variant="default">
            {text}
          </Text>
        ),
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <Text as="span" size={fontSize} variant="muted">
            {children}
          </Text>
        ),
        [INLINES.EMBEDDED_ENTRY]: node => {
          if (node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
            return <ContentfulInlineFootnote component={node.data.target} analyticsLocation={ANALYTICS_ID} />
          }

          return null
        },
      },
    })
  }

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <section id="collaboration" className="lp-Section">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-SectionTemplate">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-SectionBlock">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className="lp-SectionBlock-content">
            {sectionIntro ? (
              <SectionIntro
                title={documentToPlainTextString(sectionIntro.fields.heading)}
                description={
                  sectionIntro.fields.description ? documentToPlainTextString(sectionIntro.fields.description) : ''
                }
                mascot="mona"
                assetsRef={assetsRef}
                isWebGLMascotOnly
              />
            ) : null}

            {breakoutImage ? (
              <SectionHero
                analyticsId={ANALYTICS_ID}
                visuals={[
                  {
                    type: 'image',
                    url: {
                      desktop: breakoutImage.fields.asset.fields.file.url,
                    },
                    alt: breakoutImage.fields.description ? breakoutImage.fields.description : '',
                  },
                ]}
              />
            ) : null}
          </div>
        </div>

        {collaborationSectionBreakout ? (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="lp-SectionBlock">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <div ref={mainGridRef} className="lp-SectionBlock-content lp-SectionBlock-content--lines">
              {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
              <Grid className={`lp-SectionTemplate-grid ${!isMainGridInView ? 'lp-SectionTemplate-grid--hidden' : ''}`}>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: 6}}>
                  {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                  <div className="lp-SectionTemplate-content">
                    {collaborationSectionBreakout.fields.text ? (
                      <Heading as="h3" size="6" weight="semibold">
                        {renderRichText(collaborationSectionBreakout.fields.text)}
                      </Heading>
                    ) : null}

                    {breakoutLink ? (
                      <div>
                        <Link
                          href={breakoutLink.fields.href}
                          variant="accent"
                          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                          className="lp-SectionTemplate-content-link"
                          {...getAnalyticsEvent({
                            action: breakoutLink.fields.text,
                            tag: 'link',
                            context: 'content',
                            location: ANALYTICS_ID,
                          })}
                        >
                          <Text weight="normal" size="300">
                            {breakoutLink.fields.text}
                          </Text>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </Grid.Column>

                {testimonial ? (
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                  <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: 6}}>
                    {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                    <div className="lp-SectionTemplate-testimonial">
                      <ContentfulTestimonial component={testimonial} />
                    </div>
                  </Grid.Column>
                ) : null}
              </Grid>
            </div>
          </div>
        ) : null}

        {accordionItems.length > 0 ? <RiverAccordion items={accordionItems} analyticsId={ANALYTICS_ID} /> : null}
      </div>
    </section>
  )
}
